import { useCallback, useState } from 'react';
import { treasuryApi } from '../services/api/endpoints/treasury';
import type { TreasuryAccount, TreasuryAccountType } from '../types/treasury';
import type { CurrencyCode } from '../config/currency';

/** Payload de création d'un compte de trésorerie (front -> backend) */
export interface CreateTreasuryAccountPayload {
  name: string;
  type: TreasuryAccountType;
  accountNumber: string;
  bankName?: string;
  currency: CurrencyCode;
  isActive: boolean;
  businessUnitId?: string;
  businessUnitType?: string;
}

/**
 * Hook de gestion des comptes de trésorerie.
 *
 * Couvre les 5 types : BANK, MICROFINANCE, COOPERATIVE, VSLA, MOBILE_MONEY.
 * S'appuie sur l'API existante `treasuryApi`.
 */
export function useTreasury() {
  const [accounts, setAccounts] = useState<TreasuryAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAccounts = useCallback(
    async (params?: { type?: TreasuryAccountType; isActive?: boolean }) => {
      try {
        setLoading(true);
        setError(null);
        const response = await treasuryApi.getAccounts(params);
        const payload =
          (response as any)?.data?.data ??
          (response as any)?.data ??
          response;
        const list: TreasuryAccount[] = Array.isArray(payload)
          ? payload
          : payload?.data ?? [];
        setAccounts(list);
      } catch (err: any) {
        setError(err?.message || 'Erreur lors du chargement des comptes');
        setAccounts([]);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  const createAccount = useCallback(
    async (data: CreateTreasuryAccountPayload): Promise<TreasuryAccount | null> => {
      try {
        setSaving(true);
        setError(null);
        const response = await treasuryApi.createAccount(data as any);
        const payload =
          (response as any)?.data?.data ??
          (response as any)?.data ??
          response;
        const created: TreasuryAccount = (payload?.data ?? payload) as TreasuryAccount;
        if (created?.id) {
          setAccounts((prev) => [created, ...prev]);
        }
        return created ?? null;
      } catch (err: any) {
        setError(err?.message || 'Erreur lors de la création du compte');
        return null;
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  const deleteAccount = useCallback(async (id: string): Promise<boolean> => {
    try {
      setSaving(true);
      setError(null);
      await treasuryApi.deleteAccount(id);
      setAccounts((prev) => prev.filter((a) => a.id !== id));
      return true;
    } catch (err: any) {
      setError(err?.message || 'Erreur lors de la suppression');
      return false;
    } finally {
      setSaving(false);
    }
  }, []);

  return {
    accounts,
    loading,
    saving,
    error,
    loadAccounts,
    createAccount,
    deleteAccount,
  };
}
